const COUNT_DROPDOWN_OPTIONS = [
  "0-1", "1-2", "2-3", "3-5", "5-10", "10-20", "20-30",
  "30-50", "50-100", "100-200", ">100", ">200",
];

const ORDERED_DROPDOWN_OPTIONS = ["negative", "trace", "1+", "2+", "3+", "4+"];

const COUNT_PARAMETER_NAMES = new Set([
  "RBC (Urine)", "WBC (Urine)", "Epithelial cell", "Red Blood Cells",
  "White Blood Cells", "WBC (Stool)", "RBC (Stool)", "เซลล์เยื่อบุผิว",
  "Non-squamous epithelial cell", "Renal tubular epithelial cell",
  "Calcium oxalate crystal", "Uric acid crystal", "Triple phosphate crystal",
  "Calcium Carbonate crystal", "Calcium phosphate crystal", "Cystine crystal",
]);

const ORDERED_DROPDOWN_PARAMETER_NAMES = new Set([
  "Protein", "Sugar", "Glucose", "Ketones", "Urine Ketone", "Blood",
  "Leukocytes", "Nitrite", "Bilirubin", "Urobilinogen", "Bacteria",
]);

const PARAMETER_ALIASES = {
  คอเลสเตอรอลรวม: ["Cholesterol"],
  ไตรกลีเซอไรด์: ["Triglyceride"],
  "BUN, Urea": ["BUN"],
  ALP: ["ALP (Alkaline Phosphatase)"],
  "น้ำตาล (Sugar)": ["Sugar", "Urine Glucose"],
  Platelets: ["Platelet", "Platelets count"],
  "Platelets count": ["Platelets count", "Platelet"],
  Color: ["Urine Color"],
  pH: ["Urine pH"],
  Sugar: ["Urine Glucose"],
  Glucose: ["Urine Glucose"],
  Ketones: ["Urine Ketone"],
  Ketone: ["Urine Ketone"],
  Blood: ["Urine Blood"],
  Leukocytes: ["Urine Leukocytes"],
  Nitrite: ["Urine Nitrite"],
  Bilirubin: ["Urine Bilirubin"],
  Bacteria: ["Urine Bacteria"],
  "Other (Urine)": ["Urinalysis Others"],
  "Urine Appearance": ["Urine Appearance", "Clarity"],
  Clarity: ["Clarity", "Urine Appearance"],
  "RBC (Urine)": ["Urine RBC"],
  "WBC (Urine)": ["Urine WBC"],
  "Epithelial cell": ["Epithelial Cells", "Epithelial"],
  เซลล์เยื่อบุผิว: ["Epithelial cell", "Epithelial Cells", "Epithelial"],
  Protein: ["Urine Protein"],
  "Color (Stool)": ["Stool Color"],
  "Consistency (Stool)": ["Consistency"],
  "WBC (Stool)": ["Stool WBC"],
  "RBC (Stool)": ["Stool RBC"],
  COI: ["HCV Ab COI"],
  "methodology (HBV)": ["HBsAg methodology"],
  "methodology (HCV)": ["HCV Ab methodology"],
  "Other (Blood)": ["Others"],
};

const stripHidePrefix = (value) => String(value || "").replace(/^_hide_/i, "").trim();
const normalizeLookupKey = (value) =>
  stripHidePrefix(value).toLowerCase().replace(/[\s_-]+/g, "").trim();
const normalizeDash = (value) => String(value ?? "").replace(/[–—−]/g, "-");

const normalizeCountValue = (value) => {
  const text = normalizeDash(value).replace(/\s+/g, "").trim();
  const match = text.match(/(0-1|1-2|2-3|3-5|5-10|10-20|20-30|30-50|50-100|100-200|>100|>200)/);
  return match ? match[1] : text;
};

const normalizeOrderedDropdownValue = (value) => {
  const text = String(value ?? "").trim().toLowerCase().replace(/\s+/g, "");
  const match = text.match(/negative|trace|1\+|2\+|3\+|4\+/);
  return match ? match[0] : text;
};

const normalizeTextValue = (value) => {
  const text = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/[()[\]{}.,]/g, "")
    .replace(/\//g, "");
  const plusMatch = text.match(/([1-4])\+/);
  if (plusMatch) return `${plusMatch[1]}+`;
  const negativeAliases = new Set(["negative", "notfound", "nowbcseen", "norbcseen", "notseen", "none", "ไม่มี", "ไม่พบ"]);
  if (text.includes("negative") || negativeAliases.has(text)) return "negative";
  const positiveAliases = new Set(["positive", "found", "มี", "พบ"]);
  if (text.includes("positive") || positiveAliases.has(text)) return "positive";
  if (text.includes("trace")) return "trace";
  return text;
};

const parseNumericRange = (value) => {
  const text = normalizeDash(value).trim().replace(/,/g, "").replace(/\s+/g, "");
  if (!text) return null;
  let match = text.match(/^(-?\d+(?:\.\d+)?)-(-?\d+(?:\.\d+)?)/);
  if (match) return { min: Number(match[1]), max: Number(match[2]), openEnded: false };
  match = text.match(/^<=?(-?\d+(?:\.\d+)?)/);
  if (match) return { min: Number.NEGATIVE_INFINITY, max: Number(match[1]), openEnded: true };
  match = text.match(/^>=?(-?\d+(?:\.\d+)?)/);
  if (match) return { min: Number(match[1]), max: Number.POSITIVE_INFINITY, openEnded: true };
  match = text.match(/^(-?\d+(?:\.\d+)?)/);
  if (!match) return null;
  const numericValue = Number(match[1]);
  return { min: numericValue, max: numericValue, openEnded: false };
};

const parseTemplateBoundary = (value) => {
  const match = normalizeDash(value).trim().replace(/,/g, "").match(/-?\d+(?:\.\d+)?/);
  return match ? Number(match[0]) : null;
};

const findRecordParameter = (parameters, templateParameterName) => {
  if (!Array.isArray(parameters)) return null;
  const cleanName = stripHidePrefix(templateParameterName);
  const candidates = [cleanName, ...(PARAMETER_ALIASES[cleanName] || [])];
  const parametersByName = new Map();
  parameters.forEach((parameter) => {
    const key = normalizeLookupKey(parameter?.name);
    if (key && !parametersByName.has(key)) parametersByName.set(key, parameter);
  });
  for (const candidate of candidates) {
    const matched = parametersByName.get(normalizeLookupKey(candidate));
    if (matched) return matched;
  }
  return null;
};

const doesTemplateParameterMatch = (recordParameter, templateParameter) => {
  if (!recordParameter || !templateParameter) return false;
  const resultValue = recordParameter.result ?? recordParameter.value;
  if (resultValue === null || resultValue === undefined || resultValue === "") return false;
  const parameterName = stripHidePrefix(templateParameter.parameter_name);
  const matchType = String(templateParameter.match_type || "inside").toLowerCase();
  const customValue = String(templateParameter.custom_value ?? "").trim();
  if (customValue !== "") {
    if (normalizeTextValue(customValue) === "xxx") return true;
    return normalizeTextValue(resultValue) === normalizeTextValue(customValue);
  }
  if (COUNT_PARAMETER_NAMES.has(parameterName)) {
    const valueIdx = COUNT_DROPDOWN_OPTIONS.indexOf(normalizeCountValue(resultValue));
    const minIdx = COUNT_DROPDOWN_OPTIONS.indexOf(normalizeCountValue(templateParameter.custom_min));
    const maxIdx = COUNT_DROPDOWN_OPTIONS.indexOf(normalizeCountValue(templateParameter.custom_max));
    if (valueIdx === -1 || minIdx === -1 || maxIdx === -1) return false;
    const inside = valueIdx >= minIdx && valueIdx <= maxIdx;
    return matchType === "outside" ? !inside : inside;
  }
  if (ORDERED_DROPDOWN_PARAMETER_NAMES.has(parameterName)) {
    const valueIdx = ORDERED_DROPDOWN_OPTIONS.indexOf(normalizeOrderedDropdownValue(resultValue));
    const minIdx = ORDERED_DROPDOWN_OPTIONS.indexOf(normalizeOrderedDropdownValue(templateParameter.custom_min));
    const maxIdx = ORDERED_DROPDOWN_OPTIONS.indexOf(normalizeOrderedDropdownValue(templateParameter.custom_max));
    if (valueIdx === -1 || minIdx === -1 || maxIdx === -1) return false;
    const inside = valueIdx >= minIdx && valueIdx <= maxIdx;
    return matchType === "outside" ? !inside : inside;
  }
  const resultRange = parseNumericRange(resultValue);
  const customMin = parseTemplateBoundary(templateParameter.custom_min);
  const customMax = parseTemplateBoundary(templateParameter.custom_max);
  if (resultRange && customMin !== null && customMax !== null) {
    const inside = resultRange.openEnded
      ? !(resultRange.max < customMin || resultRange.min > customMax)
      : resultRange.min >= customMin && resultRange.max <= customMax;
    return matchType === "outside" ? !inside : inside;
  }
  const expectedText = templateParameter.custom_min ?? templateParameter.custom_max ?? "";
  if (String(expectedText).trim() === "") return false;
  const textMatches = normalizeTextValue(resultValue) === normalizeTextValue(expectedText);
  return matchType === "outside" ? !textMatches : textMatches;
};

const normalizeRecordGender = (gender) => {
  const value = String(gender ?? "").trim().toLowerCase();
  if (["ชาย", "m", "male", "1"].includes(value)) return "male";
  if (["หญิง", "f", "female", "2"].includes(value)) return "female";
  return null;
};

const getRecordAgeForMatching = (record) => {
  const rawAge = record?.preciseAge ?? record?.age;
  if (rawAge === null || rawAge === undefined || rawAge === "" || rawAge === "-") return null;
  const age = Number(rawAge);
  return Number.isFinite(age) && age >= 0 ? age : null;
};

export {
  COUNT_DROPDOWN_OPTIONS, ORDERED_DROPDOWN_OPTIONS, stripHidePrefix,
  normalizeLookupKey, normalizeCountValue, normalizeOrderedDropdownValue,
  normalizeTextValue, parseNumericRange, findRecordParameter,
  doesTemplateParameterMatch, normalizeRecordGender, getRecordAgeForMatching,
};
